import { useCheckInAppointment } from '@/api/hooks/useCheckInAppointment'
import { useConfirmPatientAppointment } from '@/api/hooks/useConfirmPatientAppointment'
import type { UpcomingVisit } from '@/api/types/ProviderApi'
import SuccessCheckmark from '@/components/svgs/SuccessCheckmark'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { HStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router } from 'expo-router'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

const CONFIRM_ERROR_TOAST_CONFIG = {
	type: 'error',
	text1: 'Failed to confirm your appointment, please try again',
}

const CONFIRM_SUCCESS_TOAST_CONFIG = {
	type: 'success',
	text1: 'Your appointment is confirmed',
}

export const RescheduleVisitButton = ({
	visit,
	variant = 'secondary',
	onInk = false,
	size,
	fullWidth = false,
	label = 'Reschedule',
}: {
	visit: UpcomingVisit
	variant?: 'secondary' | 'link' | 'ghost'
	onInk?: boolean
	size?: 'default' | 'sm' | 'lg'
	fullWidth?: boolean
	label?: string
}) => {
	// Straight into the picker: the old visit is only released once a new time is booked, so
	// there is nothing left to warn about.
	return (
		<Button
			variant={variant}
			size={size ?? (variant === 'link' ? 'default' : 'sm')}
			fullWidth={fullWidth}
			onPress={() =>
				router.push({
					pathname: '/schedule',
					params: { mode: 'reschedule', appointmentId: String(visit.appointmentId) },
				})
			}
		>
			<ButtonText className={onInk ? 'text-white' : undefined}>{label}</ButtonText>
		</Button>
	)
}

/** Confirm when the studio is waiting on it, reschedule otherwise, and the tick once confirmed. */
export const VisitActions = ({
	visit,
	onInk = false,
	layout = 'row',
}: { visit: UpcomingVisit; onInk?: boolean; layout?: 'row' | 'note' | 'hero' }) => {
	const { mutate: confirmAppointment, isPending: isConfirming } =
		useConfirmPatientAppointment()
	const { mutate: checkIn, isPending: isCheckingIn } = useCheckInAppointment()

	const onCheckIn = () => {
		analytics.trackClick('check-in-appointment', { appointmentId: visit.appointmentId })
		checkIn(
			{ appointmentId: visit.appointmentId },
			{
				// The server answers with the door's state whatever it is, so someone who was
				// already in, or a second too late, is told the truth rather than shown a failure.
				onSuccess: (next) => {
					if (next.status === 'CHECKED_IN') {
						Toast.show({ type: 'success', text1: "We know you're here" })
					}
				},
				onError: () =>
					Toast.show({
						type: 'error',
						text1: 'Could not let them know. Please try again.',
					}),
			}
		)
	}

	const onConfirm = () => {
		analytics.trackClick('confirm-appointment', {
			appointmentId: visit.appointmentId,
			appointmentTypes: visit.types.map((type) => type.value).join(','),
		})
		confirmAppointment(
			{ appointmentId: visit.appointmentId },
			{
				onSuccess: () => Toast.show(CONFIRM_SUCCESS_TOAST_CONFIG),
				onError: () => Toast.show(CONFIRM_ERROR_TOAST_CONFIG),
			}
		)
	}

	// On the home hero: the coral ask while the studio's window is open, and Move It in every
	// case. The two states that are not an ask — confirmed, and too early to be asked — are a
	// pill in the card's own header rather than a button-shaped thing that cannot be pressed.
	// The tick and the word went with it: a checkmark beside "Confirmed" beside a pill saying
	// "Confirmed" is the same fact three times.
	if (layout === 'hero') {
		return (
			<HStack gap={8} className="items-center">
				{/* Check-in outranks confirming, though the two cannot collide: confirming closes
				    four days out and the door opens thirty minutes before. Ordered anyway, so the
				    day a window moves it is the live one that wins. */}
				{visit.checkIn?.status === 'ENABLED' ? (
					<Button
						variant={onInk ? 'blush' : 'primary'}
						fullWidth={false}
						isLoading={isCheckingIn}
						onPress={onCheckIn}
					>
						{/* The same words as the visit page's slot. A member telling the studio
						    they have arrived is doing one thing, and it is worth the app calling
						    it one thing — "Check In" described our mechanism, and it no longer
						    even unlocks the building's door, which has a row of its own now. */}
						<ButtonText>I'm Here</ButtonText>
					</Button>
				) : (
					visit.isConfirmable && (
						// Blush on a photograph, coral on a card. Coral is the app's one-per-screen
						// action colour, and it earns that on the pink; laid over a warm photograph it
						// is a second warm thing and the button stops being the brightest object in
						// the frame, which is the only job it has there.
						<Button
							variant={onInk ? 'blush' : 'accent'}
							fullWidth={false}
							isLoading={isConfirming}
							onPress={onConfirm}
						>
							<ButtonText>Confirm</ButtonText>
						</Button>
					)
				)}
				<RescheduleVisitButton
					visit={visit}
					variant={onInk ? 'ghost' : 'secondary'}
					onInk={onInk}
					size="default"
					label="Move It"
				/>
			</HStack>
		)
	}

	// On the visit note: the coral button is the ask, the other thing is a text link.
	if (layout === 'note') {
		return (
			<HStack gap={16} className="items-center">
				{visit.isConfirmable ? (
					<View className="flex-1">
						<Button
							variant="accent"
							size="lg"
							fullWidth
							isLoading={isConfirming}
							onPress={onConfirm}
						>
							<ButtonText>I'll be there</ButtonText>
						</Button>
					</View>
				) : visit.isConfirmed ? (
					<HStack gap={8} className="flex-1 items-center">
						<SuccessCheckmark />
						<Text weight="bold">Confirmed</Text>
					</HStack>
				) : (
					<View className="flex-1" />
				)}
				<RescheduleVisitButton visit={visit} variant="link" label="Move it" />
			</HStack>
		)
	}

	if (visit.isConfirmable) {
		return (
			<HStack gap={16} className="items-center">
				<Button
					variant={onInk ? 'accent' : 'primary'}
					fullWidth={false}
					isLoading={isConfirming}
					onPress={onConfirm}
				>
					<ButtonText>Confirm visit</ButtonText>
				</Button>
				<RescheduleVisitButton visit={visit} variant="link" onInk={onInk} />
			</HStack>
		)
	}

	return (
		<HStack gap={16} className="items-center justify-between">
			{visit.isConfirmed ? (
				<HStack gap={8} className="items-center">
					<SuccessCheckmark />
					<Text weight="bold" tone={onInk ? 'white' : 'ink'}>
						Confirmed
					</Text>
				</HStack>
			) : (
				<View />
			)}
			<RescheduleVisitButton
				visit={visit}
				variant={onInk ? 'link' : 'secondary'}
				onInk={onInk}
			/>
		</HStack>
	)
}
